<template>
  <div
    class="requirement cursor-pointer rounded-xl border-2 border-dashed border-transparent focus-within:border-brand-contrast focus-within:border-dashed p-4 active:border-transparent"
    :class="{ selected: userHasSelected }"
    @focusin="setIsFocused(true)"
    @focusout="setIsFocused(false)"
    @mouseenter="setIsHovered(true)"
    @mouseleave="setIsHovered(false)"
  >
    <div v-if="!isButton">
      <input :id="value" type="checkbox" class="sr-only" :value="value" :checked="userHasSelected" @change="updateSelections" />
      <label :for="value" class="flex flex-col items-center centered cursor-pointer" @click="setClicked(true)">
        <div aria-hidden="true">
          <slot name="graphic" />
        </div>
        <span class="text-subtitle my-8 cursor-pointer" :class="labelClasses">
          <slot />
        </span>
      </label>
    </div>
    <button
      v-else
      :id="value"
      :value="value"
      type="button"
      class="outline-none cursor-pointer flex flex-col items-center centered m-auto"
      @click="buttonClicked"
      @mousedown="setClicked(true)"
      @mouseup="setClicked(true)"
    >
      <div aria-hidden="true" class="inline-block">
        <slot name="graphic" />
      </div>
      <p class="text-subtitle my-8 cursor-pointer" :class="labelClasses">
        <slot />
      </p>
    </button>
  </div>
</template>

<script lang="ts">
export default {
  name: 'Requirement',
  model: {
    prop: 'requirementsSelections',
    event: 'change',
  },
  props: {
    isButton: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    requirementsSelections: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      wasClicked: false,
      isFocused: false,
      isHovered: false,
    };
  },
  computed: {
    userHasSelected() {
      return this.selected || this.requirementsSelections.includes(this.value);
    },
    labelClasses() {
      if (this.userHasSelected) return 'text-primary';
      if (this.isHovered || this.isFocused) return 'text-brand-contrast';
      return 'text-grey-text-subdued';
    },
  },
  methods: {
    buttonClicked() {
      this.$emit('click');
      if (this.wasClicked) {
        this.removeMouseFocus();
      }
    },
    setClicked(clicked) {
      this.wasClicked = clicked;
    },
    setIsFocused(focused) {
      this.isFocused = focused;
    },
    setIsHovered(hovered) {
      this.isHovered = hovered;
    },
    updateSelections(event) {
      const isChecked = event.target.checked;
      const newSelections = [...this.requirementsSelections];
      if (isChecked) {
        newSelections.push(this.value);
      } else {
        newSelections.splice(newSelections.indexOf(this.value), 1);
      }
      this.$emit('change', newSelections);

      if (this.wasClicked) {
        this.removeMouseFocus();
      }
    },
    removeMouseFocus() {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      this.wasClicked = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.requirement::v-deep circle {
  stroke-width: 1;
  stroke: theme('colors.grey.divider.contrast');
  fill: 'white';
}
.requirement::v-deep path {
  fill: theme('colors.grey.graphics.subdued');
}
.requirement:not(.selected):hover,
.requirement:not(.selected):focus-within {
  &::v-deep circle {
    stroke-width: 1;
    stroke: theme('colors.brand.contrast');
    fill: 'white';
  }
  &::v-deep path {
    fill: theme('colors.brand.contrast');
  }
}
.requirement.selected::v-deep circle {
  stroke-width: 2;
  stroke: theme('colors.primary.DEFAULT');
  fill: theme('colors.primary.subdued');
}
.requirement.selected::v-deep path {
  fill: theme('colors.primary.DEFAULT');
}
</style>
